/**
 * QuoteDeliveryStatus — per-channel send outcome on quote detail.
 */

import React from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Typography,
} from "@mui/material";
import { Email, WhatsApp } from "@mui/icons-material";
import { format } from "date-fns";

const STATUS_COLOR = {
  sent: "success",
  queued: "info",
  skipped: "default",
  failed: "error",
  queue_failed: "error",
  pending: "warning",
  complete: "success",
  partial: "warning",
};

function channelLabel(status) {
  if (!status) return "Unknown";
  return String(status).replace(/_/g, " ");
}

function formatAt(at) {
  if (!at) return null;
  try {
    return format(new Date(at), "PPp");
  } catch {
    return null;
  }
}

function ChannelRow({ icon, title, channel }) {
  if (!channel?.status) return null;
  const when = formatAt(channel.at);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        py: 1,
        borderBottom: "1px solid",
        borderColor: "divider",
        "&:last-child": { borderBottom: 0 },
      }}
    >
      {icon}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="subtitle2">{title}</Typography>
          <Chip
            size="small"
            label={channelLabel(channel.status)}
            color={STATUS_COLOR[channel.status] || "default"}
            variant="outlined"
          />
        </Box>
        {channel.to && (
          <Typography variant="body2" color="text.secondary">
            {channel.to}
          </Typography>
        )}
        {when && (
          <Typography variant="caption" color="text.secondary">
            {when}
          </Typography>
        )}
        {(channel.error || channel.reason) && (
          <Typography variant="caption" color="error" component="div" sx={{ mt: 0.5 }}>
            {channel.error || channel.reason}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default function QuoteDeliveryStatus({ lastDelivery, quoteStatus }) {
  const delivery = lastDelivery;
  const overall = delivery?.overall;
  const channels = delivery?.channels || {};
  const campaignId = delivery?.campaignId;

  if (!delivery && quoteStatus !== "send_queued") {
    return null;
  }

  const showPartialWarning =
    overall === "partial" || overall === "failed" ||
    channels.email?.status === "failed" ||
    channels.email?.status === "queue_failed";

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
          <Typography variant="h6" component="h3">
            Delivery status
          </Typography>
          {overall && (
            <Chip
              size="small"
              label={channelLabel(overall)}
              color={STATUS_COLOR[overall] || "default"}
            />
          )}
          {campaignId && (
            <Typography variant="caption" color="text.secondary">
              Campaign {String(campaignId).slice(0, 8)}…
            </Typography>
          )}
          {quoteStatus === "send_queued" && !overall && (
            <Chip size="small" label="scheduled" color="info" variant="outlined" />
          )}
        </Box>

        {showPartialWarning && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Not all channels delivered successfully. Check email configuration or
            resend the failed channel.
          </Alert>
        )}

        {quoteStatus === "send_queued" && !channels.whatsapp && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Send is queued — WhatsApp and email go out after the undo window unless
            cancelled.
          </Typography>
        )}

        <ChannelRow
          icon={<WhatsApp color="success" fontSize="small" />}
          title="WhatsApp"
          channel={channels.whatsapp}
        />
        <ChannelRow
          icon={<Email color="primary" fontSize="small" />}
          title="Email"
          channel={channels.email}
        />

        {!channels.whatsapp && !channels.email && quoteStatus !== "send_queued" && (
          <Typography variant="body2" color="text.secondary">
            No delivery recorded yet.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
